import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  SimpleGrid,
  Icon,
  useColorModeValue,
} from '@chakra-ui/react';
import {
  ChevronRightIcon,
  StarIcon,
  SettingsIcon,
  InfoIcon,
  CheckCircleIcon,
} from '@chakra-ui/icons';
import { HeroLogo } from '../components/SpeedcodeLogo';
import { AuthDebugPanel } from '../components/AuthDebugPanel';

export default function LandingPage() {
  // Theme-aware color variables
  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const sectionBg = useColorModeValue('white', 'gray.800');
  const cardBg = useColorModeValue('white', 'gray.700');
  const cardBorder = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.900', 'gray.100');
  const mutedTextColor = useColorModeValue('gray.600', 'gray.400');
  const iconBg = useColorModeValue('brand.50', 'brand.900');
  const iconColor = useColorModeValue('brand.500', 'brand.200');



  const compactFeatures = [
    {
      icon: SettingsIcon,
      title: 'Parallel Programming',
      description: 'Solve real multicore challenges with OpenCilk and measure actual performance gains.',
      color: 'brand',
    },
    {
      icon: StarIcon,
      title: 'Real Performance Metrics',
      description: 'Get instant feedback on execution time, memory usage, and scalability analysis.',
      color: 'secondary',
    },
    {
      icon: CheckCircleIcon,
      title: 'Scalable Solutions',
      description: 'Learn to write code that scales efficiently across multiple cores and systems.',
      color: 'accent',
    },
    {
      icon: InfoIcon,
      title: 'Competitive Learning',
      description: 'Compete with developers worldwide and climb the performance engineering leaderboard.',
      color: 'success',
    },
  ];

  return (
    <Box bg={bgColor} minH="100vh">
      {/* UI Redesign Banner */}
      <Box bg="purple.500" color="white" py={2} textAlign="center" position="relative" zIndex={10}>
        <Container maxW="container.xl">
          <HStack justify="center" spacing={4}>
            <Text fontSize="sm" fontWeight="medium">
              🚀 Checkout the Fastcode OSE
            </Text>
            <Button
              as="a"
              href="https://fastcode.org"
              target="_blank"
              rel="noopener noreferrer"
              size="xs"
              colorScheme="whiteAlpha"
              variant="outline"
              rightIcon={<ChevronRightIcon />}
              _hover={{ bg: 'whiteAlpha.200' }}
            >
              Learn More
            </Button>
          </HStack>
        </Container>
      </Box>

      {/* Hero Section */}
      <Box
        bg={useColorModeValue(
          "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          "linear-gradient(135deg, #2d3561 0%, #3b2c5a 50%, #4a3b6b 100%)"
        )}
        color="white"
        pt={4}
        pb={32}
        position="relative"
        overflow="hidden"
      >
        {/* Dot Pattern Overlay */}
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          opacity={0.2}
          bg={useColorModeValue("white", "blackAlpha.300")}
          backgroundImage={useColorModeValue(
            "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.3) 1px, transparent 1px)",
            "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.1) 1px, transparent 1px)"
          )}
          backgroundSize="40px 40px"
        />
        
        {/* Additional Gradient Overlays */}
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          background={`
            radial-gradient(circle at 20% 80%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.08) 0%, transparent 50%),
            radial-gradient(circle at 40% 40%, rgba(255, 255, 255, 0.05) 0%, transparent 50%)
          `}
          pointerEvents="none"
        />
        
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          background="linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.1) 100%)"
          pointerEvents="none"
        />
        <Container maxW="container.xl" position="relative" zIndex={1}>
          <VStack spacing={16} textAlign="center">
            {/* Main Hero Logo with Balanced Shadow */}
            <VStack spacing={8} w="full">
              <Box
                _hover={{ transform: 'scale(1.01)' }}
                transition="transform 0.3s ease"
                filter="drop-shadow(0 10px 25px rgba(0,0,0,0.35)) drop-shadow(0 5px 15px rgba(0,0,0,0.25))"
                opacity="1"
              >
                <HeroLogo />
              </Box>
            </VStack>

            {/* Main Content */}
            <VStack spacing={10} maxW="4xl" mx="auto">
              <VStack spacing={6}>
                <Heading
                  size="2xl"
                  fontWeight="900"
                  letterSpacing="-0.03em"
                  lineHeight="1.1"
                  color="white"
                  textShadow="0 4px 12px rgba(0,0,0,0.6), 0 2px 4px rgba(0,0,0,0.8)"
                >
                  Master Performance Engineering
                </Heading>
                <Heading
                  size="lg"
                  fontWeight="600"
                  letterSpacing="-0.01em"
                  color="white"
                  textShadow="0 3px 8px rgba(0,0,0,0.5), 0 1px 3px rgba(0,0,0,0.7)"
                >
                  Real-world parallel programming challenges with instant feedback
                </Heading>
              </VStack>

              <Text
                fontSize="xl"
                maxW="3xl"
                lineHeight="1.8"
                color="white"
                fontWeight="500"
                textShadow="0 2px 6px rgba(0,0,0,0.4), 0 1px 2px rgba(0,0,0,0.6)"
              >
                Master parallel programming and performance engineering with real-world challenges. 
                Solve practical problems, measure actual performance gains, and compete with developers worldwide.
              </Text>



              {/* CTAs */}
              <VStack spacing={6} pt={4}>
                <HStack spacing={4}>
                  <Button
                    as={RouterLink}
                    to="/problems"
                    size="lg"
                    bg="white"
                    color="brand.700"
                    px={10}
                    py={7}
                    fontSize="lg"
                    fontWeight="700"
                    borderRadius="xl"
                    border="1px solid"
                    borderColor="brand.200"
                    _hover={{
                      bg: 'gray.50',
                      color: 'brand.800',
                      transform: 'translateY(-1px)',
                      boxShadow: '0 12px 40px rgba(0,0,0,0.2)',
                      borderColor: 'brand.300',
                    }}
                    _active={{
                      transform: 'translateY(0px)',
                      boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                    }}
                    transition="all 0.3s ease"
                    leftIcon={<StarIcon />}
                  >
                    Try Free Challenge
                  </Button>
                  <Button
                    size="lg"
                    colorScheme="whiteAlpha"
                    variant="outline"
                    px={10}
                    py={7}
                    fontSize="lg"
                    fontWeight="600"
                    borderRadius="xl"
                    borderWidth="2px"
                    _hover={{
                      bg: 'white',
                      color: 'brand.500',
                      transform: 'translateY(-1px)',
                      boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
                    }}
                    _active={{
                      transform: 'translateY(0px)',
                      boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
                    }}
                    transition="all 0.3s ease"
                    rightIcon={<ChevronRightIcon />}
                  >
                    See How It Works
                  </Button>
                </HStack>
                
                {/* Trust Indicators */}
                <Box
                  bg={useColorModeValue(
                    "rgba(255, 255, 255, 0.95)",
                    "rgba(0, 0, 0, 0.8)"
                  )}
                  backdropFilter="blur(20px)"
                  borderRadius="2xl"
                  px={6}
                  py={3}
                  border="2px solid"
                  borderColor={useColorModeValue(
                    "rgba(255, 255, 255, 0.9)",
                    "rgba(255, 255, 255, 0.4)"
                  )}
                  boxShadow={useColorModeValue(
                    "0 10px 25px rgba(0, 0, 0, 0.1)",
                    "0 10px 25px rgba(0, 0, 0, 0.3)"
                  )}
                >
                  <HStack 
                    spacing={6} 
                    color={useColorModeValue("gray.700", "white")}
                    fontSize="xs" 
                    fontWeight="700"
                    divider={
                      <Box 
                        w="2px" 
                        h="4" 
                        bg={useColorModeValue("gray.400", "rgba(255,255,255,0.8)")} 
                        borderRadius="full" 
                      />
                    }
                  >
                    <HStack spacing={1}>
                      <Text fontWeight="bold" color={useColorModeValue("green.600", "green.300")}>✓</Text>
                      <Text color={useColorModeValue("gray.700", "white")}>Free to start</Text>
                    </HStack>
                    <HStack spacing={1}>
                      <Text fontWeight="bold" color={useColorModeValue("green.600", "green.300")}>✓</Text>
                      <Text color={useColorModeValue("gray.700", "white")}>No setup required</Text>
                    </HStack>
                    <HStack spacing={1}>
                      <Text fontWeight="bold" color={useColorModeValue("green.600", "green.300")}>✓</Text>
                      <Text color={useColorModeValue("gray.700", "white")}>Instant feedback</Text>
                    </HStack>
                  </HStack>
                </Box>
              </VStack>
            </VStack>
          </VStack>
        </Container>
      </Box>

      {/* Elevated Features Section */}
      <Container maxW="container.xl" mt={-16} position="relative" zIndex={1}>
        <SimpleGrid columns={{ base: 2, md: 4 }} spacing={6}>
          {compactFeatures.map((feature, index) => (
            <Box
              key={index}
              bg={cardBg}
              p={6}
              borderRadius="2xl"
              boxShadow="xl"
              border="1px solid"
              borderColor={cardBorder}
              position="relative"
              overflow="hidden"
              transition="all 0.3s ease"
              _hover={{ 
                transform: 'translateY(-8px)', 
                boxShadow: useColorModeValue(
                  '0 25px 50px -12px rgba(0, 0, 0, 0.15)',
                  '0 25px 50px -12px rgba(0, 0, 0, 0.5)'
                ),
                borderColor: `${feature.color}.400`,
              }}
            >
              {/* Decorative Background Element */}
              <Box
                position="absolute"
                top={0}
                right={0}
                w="100px"
                h="100px"
                bg={`${feature.color}.400`}
                opacity={0.1}
                borderRadius="full"
                transform="translate(30px, -30px)"
              />
              
              <VStack spacing={4} position="relative" textAlign="center">
                <Box
                  p={3}
                  borderRadius="xl"
                  bg={`${feature.color}.100`}
                  color={`${feature.color}.600`}
                  boxShadow="lg"
                  border="1px solid"
                  borderColor={`${feature.color}.200`}
                >
                  <Icon as={feature.icon} boxSize={6} />
                </Box>
                <VStack spacing={2}>
                  <Heading size="md" fontWeight="700" color={textColor} textAlign="center">
                    {feature.title}
                  </Heading>
                  <Text 
                    color={mutedTextColor} 
                    fontSize="sm" 
                    fontWeight="500" 
                    textAlign="center"
                    lineHeight="1.5"
                  >
                    {feature.description}
                  </Text>
                </VStack>
              </VStack>
            </Box>
          ))}
        </SimpleGrid>
      </Container>

      {/* Section Divider */}
      <Box py={16} bg={sectionBg}>
        <Container maxW="container.xl">
          <VStack spacing={4} textAlign="center">
            <Heading size="lg" color={textColor} fontWeight="bold">
              Join a growing community of performance engineers joining the Fastcode OSE
            </Heading>
            <Text fontSize="md" color={mutedTextColor} maxW="2xl">
              Learn more at{' '}
              <Text as="a" href="https://fastcode.org" target="_blank" rel="noopener noreferrer" color="brand.500" fontWeight="600" textDecoration="underline" _hover={{ color: 'brand.600' }}>
                fastcode.org
              </Text>
            </Text>
          </VStack>
        </Container>
      </Box>



      {/* CTA Section */}
      <Box 
        py={20} 
        bg={useColorModeValue(
          "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          "linear-gradient(135deg, #2d3561 0%, #3b2c5a 50%, #4a3b6b 100%)"
        )} 
        color="white"
        position="relative"
        overflow="hidden"
      >
        {/* Background Pattern */}
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          opacity={0.1}
          backgroundImage={useColorModeValue(
            "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.3) 1px, transparent 1px)",
            "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.2) 1px, transparent 1px)"
          )}
          backgroundSize="30px 30px"
        />
        
        {/* Gradient Overlay */}
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          background={`
            radial-gradient(circle at 30% 40%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
            radial-gradient(circle at 70% 60%, rgba(255, 255, 255, 0.08) 0%, transparent 50%)
          `}
          pointerEvents="none"
        />
        <Container maxW="container.xl">
          <VStack spacing={10} textAlign="center">
            <VStack spacing={6}>
              <Heading 
                size="xl" 
                fontWeight="bold"
                color="white"
                textShadow="0 4px 12px rgba(0,0,0,0.8), 0 2px 6px rgba(0,0,0,0.9)"
              >
                Start Your Performance Engineering Journey
              </Heading>
              <Text 
                fontSize="lg" 
                maxW="2xl" 
                fontWeight="500"
                color="white"
                textShadow="0 3px 8px rgba(0,0,0,0.7), 0 1px 4px rgba(0,0,0,0.8)"
              >
                Master parallel programming and performance engineering with hands-on challenges 
                designed to advance your career in high-performance computing.
              </Text>
            </VStack>

            {/* Trust Indicators */}
            <Box
              bg={useColorModeValue(
                "rgba(255, 255, 255, 0.95)",
                "rgba(0, 0, 0, 0.85)"
              )}
              backdropFilter="blur(20px)"
              borderRadius="3xl"
              px={8}
              py={4}
              border="2px solid"
              borderColor={useColorModeValue(
                "rgba(255, 255, 255, 0.9)",
                "rgba(255, 255, 255, 0.5)"
              )}
              boxShadow={useColorModeValue(
                "0 15px 35px rgba(0, 0, 0, 0.1)",
                "0 15px 35px rgba(0, 0, 0, 0.4)"
              )}
            >
              <HStack 
                spacing={8} 
                color={useColorModeValue("gray.700", "white")}
                fontSize="sm" 
                fontWeight="700"
                divider={
                  <Box 
                    w="2px" 
                    h="5" 
                    bg={useColorModeValue("gray.400", "rgba(255,255,255,0.8)")} 
                    borderRadius="full" 
                  />
                }
              >
                <HStack spacing={2}>
                  <Text fontWeight="bold" color={useColorModeValue("green.600", "green.300")}>Free</Text>
                  <Text color={useColorModeValue("gray.700", "white")}>to start</Text>
                </HStack>
                <HStack spacing={2}>
                  <Text fontWeight="bold" color={useColorModeValue("blue.600", "blue.300")}>No setup</Text>
                  <Text color={useColorModeValue("gray.700", "white")}>required</Text>
                </HStack>
                <HStack spacing={2}>
                  <Text fontWeight="bold" color={useColorModeValue("purple.600", "purple.300")}>Instant</Text>
                  <Text color={useColorModeValue("gray.700", "white")}>feedback</Text>
                </HStack>
              </HStack>
            </Box>

            <HStack spacing={4} position="relative" zIndex={1}>
              <Button
                as={RouterLink}
                to="/problems"
                size="xl"
                bg="white"
                color="brand.800"
                px={12}
                py={8}
                fontSize="lg"
                fontWeight="700"
                borderRadius="2xl"
                boxShadow="0 10px 25px rgba(0, 0, 0, 0.15)"
                border="2px solid"
                borderColor="brand.200"
                _hover={{
                  bg: 'gray.50',
                  color: 'brand.900',
                  transform: 'translateY(-4px) scale(1.05)',
                  boxShadow: '0 20px 40px rgba(0, 0, 0, 0.2)',
                  borderColor: 'brand.300',
                }}
                _active={{
                  transform: 'translateY(-2px) scale(1.02)',
                }}
                transition="all 0.4s cubic-bezier(0.4, 0, 0.2, 1)"
                leftIcon={<StarIcon />}
              >
                Start Free Challenge
              </Button>
              <Button
                size="xl"
                variant="outline"
                px={12}
                py={8}
                fontSize="lg"
                fontWeight="600"
                borderRadius="2xl"
                borderWidth="2px"
                borderColor="whiteAlpha.400"
                color="white"
                bg="whiteAlpha.100"
                backdropFilter="blur(10px)"
                boxShadow="0 10px 25px rgba(0, 0, 0, 0.1)"
                _hover={{
                  bg: 'white',
                  color: 'brand.500',
                  transform: 'translateY(-4px) scale(1.05)',
                  boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
                  borderColor: 'white',
                }}
                _active={{
                  transform: 'translateY(-2px) scale(1.02)',
                }}
                transition="all 0.4s cubic-bezier(0.4, 0, 0.2, 1)"
                rightIcon={<ChevronRightIcon />}
              >
                View Leaderboard
              </Button>
            </HStack>
          </VStack>
        </Container>
      </Box>

      {/* Auth Debug Panel - Development Only */}
      <Box py={10} bg={bgColor}>
        <Container maxW="container.md">
          <AuthDebugPanel />
        </Container>
      </Box>
    </Box>
  );
} 